import { Signal, SignalRow, Tag, type SignalTone } from "@diametral/design-system/react";

import type { DashboardAlert, StreamStatus, Weather } from "../api/types";
import { formatDate } from "../lib/format";
import { t, useTranslation } from "../lib/i18n";

/** Status order of the share bar and the legend: what needs attention first. */
export const STATUS_ORDER: Array<StreamStatus | "none"> = ["blocked", "in_progress", "not_started", "done", "none"];

const STATUS_TONE: Record<StreamStatus | "none", SignalTone> = {
  blocked: "blocked",
  in_progress: "progress",
  not_started: "idle",
  done: "done",
  none: "empty",
};

export function statusLabel(status: StreamStatus | "none" | null): string {
  switch (status ?? "none") {
    case "blocked":
      return t("Blocked");
    case "in_progress":
      return t("In progress");
    case "not_started":
      return t("Not started");
    case "done":
      return t("Done");
    default:
      return t("No status");
  }
}

export function statusTone(status: StreamStatus | null): SignalTone {
  return STATUS_TONE[status ?? "none"];
}

export function StatusSignal({ status }: { status: StreamStatus | null }) {
  return <Signal tone={statusTone(status)}>{statusLabel(status)}</Signal>;
}

const WEATHER_TONE: Record<Weather, SignalTone> = { green: "done", orange: "warn", red: "blocked" };

export function weatherLabel(weather: Weather): string {
  if (weather === "green") return t("Under control");
  if (weather === "orange") return t("To watch");
  return t("In difficulty");
}

export function WeatherSignal({ weather }: { weather: Weather | null }) {
  if (!weather) return null;
  return (
    <Signal tone={WEATHER_TONE[weather]} square>
      {weatherLabel(weather)}
    </Signal>
  );
}

export function PriorityTag({ priority }: { priority: number | null }) {
  if (priority === null || priority === undefined) return null;
  return <Tag tone={priority <= 1 ? "info" : "neutral"}>P{priority}</Tag>;
}

/** One bare dot per stream, the stream and its status in the tooltip. */
export function StreamDots({ streams }: { streams: Array<{ id: number; title: string; status: StreamStatus | null }> }) {
  return (
    <SignalRow>
      {streams.map((s) => (
        <Signal key={s.id} tone={statusTone(s.status)} title={`${s.title} : ${statusLabel(s.status)}`} />
      ))}
    </SignalRow>
  );
}

/** Dashboard alerts arrive structured; they are phrased here, in the reader's language. */
export function alertText(a: DashboardAlert): { title: string; detail: string } {
  const where = a.project && a.stream ? `${a.project.title} · ${a.stream.title}` : (a.project?.title ?? "");
  switch (a.kind) {
    case "stream_blocked":
      return { title: t("{{where}} is blocked.", { where }), detail: a.pain_point || t("No explanation entered in the blocking point.") };
    case "stream_red":
      return { title: t("{{where}} is reported in difficulty.", { where }), detail: a.pain_point || t("Red weather without a comment.") };
    case "launch_overdue":
      return {
        title: t("{{where}}: launch planned on {{date}}.", { where, date: formatDate(a.launch_date) }),
        detail: a.status
          ? t("Current status: {{status}}. Confirm the delivery or reschedule.", { status: statusLabel(a.status) })
          : t("No status. Delivered or late?"),
      };
    case "stale":
      return { title: t("{{where}} has not been updated for {{count}} days.", { where, count: a.days ?? 0 }), detail: t("Ask the owner for an update.") };
    case "ipc_date_mismatch":
      return {
        title: a.planned
          ? t("{{where}}: committee date expected on {{date}}, planned on {{planned}}.", {
              where,
              date: formatDate(a.target),
              planned: formatDate(a.planned),
            })
          : t("{{where}}: committee date expected on {{date}}, not planned.", { where, date: formatDate(a.target) }),
        detail: t("Align the stream or the planning."),
      };
    case "no_passage":
      return {
        title: t("{{where}} has no committee slot planned.", { where }),
        detail: a.project?.priority !== null && a.project?.priority !== undefined
          ? t("Priority {{priority}}. Add it to a session in the planning.", { priority: a.project.priority })
          : t("Add it to a session in the planning."),
      };
    case "missing_status":
      return { title: t("{{where}}: {{count}} streams without a status.", { where, count: a.count ?? 0 }), detail: t("To fill in at the next slot.") };
    case "incomplete":
      return {
        title: t("{{where}}: {{count}} streams without a PO and a tech lead.", { where, count: a.count ?? 0 }),
        detail: t("The DAR appoints them on the project page."),
      };
    case "unknown_slot":
      return {
        title: t("“{{label}}” is planned but is not a known project.", { label: a.label ?? "" }),
        detail: t("Create the project or remove the slot."),
      };
    case "actions_late":
      return { title: t("{{count}} late actions.", { count: a.count ?? 0 }), detail: (a.sample ?? []).join(" · ") };
    case "actions_no_owner":
      return {
        title: t("{{count}} actions without an owner.", { count: a.count ?? 0 }),
        detail: t("Give them an owner and a due date in the minutes."),
      };
    default:
      return { title: a.kind, detail: "" };
  }
}

export function RoleList({ label, people }: { label: string; people: Array<{ id: number; name: string }> }) {
  const { t } = useTranslation();
  return (
    <span className="ada-pilot-role">
      <span className="ada-faint">{label}</span> {people.length ? people.map((p) => p.name).join(", ") : <em className="ada-faint">{t("none")}</em>}
    </span>
  );
}
